import React, { useCallback, useMemo } from 'react';
import { EditRedistributionData } from '../editRedistributionLib';
import PrefilledTableRoute from '../../Statistics/modal/Routes/PrefilledTableRoute';
import { Column } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useCurrency } from '../../../../hooks/useCurrency';
import StatisticsSummary from '../StatisticsSummary';
import { useApplicationData } from '../../../../hooks/useApplicationData';
import { useDeleteRoutesMutation } from '../apiCalculationEdit';
import { useAppDispatch } from '../../../../store';
import { closeModal } from '../../../Modals/modalSlice';
import { processErrorAndShowToast } from '../../../../lib/utils/processErrorAndShowToast';
import BackupOrRemoveButton from '../BackupOrRemoveButton';
import { buildRouteStats } from './routeStats';
import { computeRemSize } from '@ydistri/theme';

type EditRedistributionRouteProps = Pick<
  EditRedistributionData,
  'calculationId' | 'routes' | 'useDepartments'
>;

const EditRedistributionRoute: React.FC<EditRedistributionRouteProps> = ({
  calculationId,
  routes,
  useDepartments,
}) => {
  const currency = useCurrency();
  const dispatch = useAppDispatch();
  const { departments: hasDepartments } = useApplicationData();
  const [deleteRoutes, deleteRoutesStatus] = useDeleteRoutesMutation();

  const handleDelete = useCallback(() => {
    deleteRoutes({
      calculationId,
      routes: (routes ?? []).map((r, i) => ({
        sourceStoreId: r.sourceStore.id,
        targetStoreId: r.targetStore.id,
        departmentId: r.department?.id,
        description: i === 0 ? `Removed ${routes?.length} routes` : undefined,
        entityDetail: JSON.stringify(r),
      })),
    })
      .unwrap()
      .then(() => {
        dispatch(addToast({ message: `Routes removed successfully.` }));
        dispatch(closeModal());
      })
      .catch(processErrorAndShowToast(dispatch, 'Removing routes failed'));
  }, [deleteRoutes, calculationId, routes, dispatch]);

  const stats = useMemo(() => buildRouteStats(routes, currency), [routes, currency]);

  return (
    <Column $gap="2rem" $justifyContent="space-evenly" $padding={0}>
      <div style={{ minHeight: computeRemSize(400) }}>
        <PrefilledTableRoute data={routes} displayDepartment={hasDepartments && useDepartments} />
      </div>
      <StatisticsSummary title="Summary" stats={stats} />
      <BackupOrRemoveButton
        calculationId={calculationId}
        loading={deleteRoutesStatus.isLoading}
        onClick={handleDelete}
      />
    </Column>
  );
};

export default EditRedistributionRoute;
